import React, { forwardRef, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { getPathname } from '../../utils/helpers/url';
import { setChosenLocationNames, setLocationIds } from '../../actions';
import { NewSearch } from '../NewSearch';
import { FallbackView } from '../FallbackView';
import { GeneralAdsPageProps } from './types';
import {
  VippedItemsSection,
  VippedItemsSectionRef
} from './VippedItemsSection';
import {
  VippedResidencesItemsSection,
  VippedResidencesItemsSectionRef
} from './VippedResidencesItemsSection';
import {
  RegularItemsSection,
  RegularItemsSectionRef
} from './RegularItemsSection';
import { getChosenLocationNames } from '../../utils/helpers/getChosenLocationNames';
import { HomeLayoutWithBanner } from '../HomeLayoutWithBanner';
import { capitalizeFirstLetter } from '../../utils/helpers/common';
import { SearchBreadcrumbsContainer, SearchPageTitle } from './styled';
import {
  ANY_CITY_ID,
  SEARCH_RESULT_ROUTES
} from '../../utils/helpers/constants';
import { selectSearchFormCurrentCity } from '../../store/selectors';
import { useCityAndLocationsQuery } from '../../hooks/useCityAndLocationsQuery';
import useScreenSize from '../../hooks/useScreenSize';
import { useClient } from '../../hooks/useClient';
import { Location } from '../../graphql/graphql';
import { Breadcrumbs } from '../Breadcrumbs';
import { PopularRequests } from '../PopularRequests';
import { SearchFormValues } from '../../utils/helpers/search';

const Banner = dynamic(() => import('../Banner'), { ssr: false });
const AdManagerBanner = dynamic(() => import('../Banner/AdManagerBanner'), {
  ssr: false
});

export const GeneralAdsPage = forwardRef<
  { reset: (values: SearchFormValues) => void },
  GeneralAdsPageProps
>(({ transformedObj, setErrorUI, h1, totalAdsCount, breadcrumbs }, ref) => {
  const { isSmall } = useScreenSize();
  const router = useRouter();
  const { t } = useTranslation();
  const params = useSearchParams();
  const dispatch = useDispatch();
  const isClient = useClient();
  const currentCity: string = useSelector(selectSearchFormCurrentCity);

  const isItemsAllPage = router.pathname === SEARCH_RESULT_ROUTES.ITEMS_ALL;
  const isSearchPage = router.pathname === '/[...search]';
  const vippedItemsSectionRef = useRef<VippedItemsSectionRef | null>(null);
  const vippedResidencesItemsSectionRef =
    useRef<VippedResidencesItemsSectionRef | null>(null);
  const regularItemsSectionRef = useRef<RegularItemsSectionRef | null>(null);

  const locationIds = useMemo(
    () => params.getAll('location_ids[]'),
    [params.getAll]
  );

  const cityId = currentCity || ANY_CITY_ID;
  const { data } = useCityAndLocationsQuery(cityId);
  const locations = data?.locations || [];

  useEffect(() => {
    if (locationIds.length) {
      const locationNames = getChosenLocationNames(
        locations as Location[],
        locationIds
      );
      dispatch(setChosenLocationNames(locationNames));
      dispatch(setLocationIds(locationIds));
    }
  }, [locationIds, data]);

  useEffect(() => {
    router.beforePopState(({ options }) => {
      options.scroll = false;

      return true;
    });
  }, []);

  const handleQueryError = () => setErrorUI(true);

  const Error = () => (
    <FallbackView
      width={317}
      height={230}
      text={t('search.no_results.text')}
      icon="/static/images/new_ad/icons/icon-not-found.png"
    />
  );

  const refetchAllSearchQueries = async () => {
    await regularItemsSectionRef.current?.refetch();
    if (!isItemsAllPage) {
      await vippedItemsSectionRef.current?.refetch();
      await vippedResidencesItemsSectionRef.current?.refetch();
    }
  };

  return (
    <>
      {!isItemsAllPage && (
        <SearchBreadcrumbsContainer>
          <Breadcrumbs
            breadcrumbs={breadcrumbs || []}
            cypress="search-result-breadcrumbs"
          />
        </SearchBreadcrumbsContainer>
      )}
      <NewSearch
        formElToAssign={ref}
        isHomePage={false}
        refetchAllSearchQueries={refetchAllSearchQueries}
      />
      <HomeLayoutWithBanner>
        {h1 && (
          <SearchPageTitle tag="h1" data-cy="search-page-title">
            {capitalizeFirstLetter(h1)}
          </SearchPageTitle>
        )}
        {totalAdsCount === 0 ? (
          <Error />
        ) : (
          <>
            <VippedItemsSection
              ref={vippedItemsSectionRef}
              filter={transformedObj.filter}
              onError={handleQueryError}
            />
            <VippedResidencesItemsSection
              ref={vippedResidencesItemsSectionRef}
              filter={transformedObj.filter}
              onError={handleQueryError}
            />
            {isSearchPage && isSmall && isClient && (
              <>
                <Banner
                  key="lotriver-top-banner"
                  containerClassName={'lotriver-top-banner'}
                  containerId={'js-lotriver-top-banner'}
                  sid={'220686'}
                  bn={'2'}
                  arCn={1}
                />
                <AdManagerBanner bannerName={'top'} />
              </>
            )}
            <RegularItemsSection
              ref={regularItemsSectionRef}
              filter={transformedObj.filter}
              sorting={transformedObj.sorting}
              onError={handleQueryError}
            />
          </>
        )}
        <PopularRequests path={getPathname(router.asPath)} />
      </HomeLayoutWithBanner>
    </>
  );
});

GeneralAdsPage.displayName = 'GeneralAdsPage';
